import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Bell, RefreshCw, LogIn, UserPlus, Trophy, ClipboardList, CalendarCheck, DollarSign, Coins, GraduationCap, Phone, Brain, Search, Wifi, Clock, ChevronDown } from "lucide-react";
import Avatar from "@/components/Avatar";
import JourneyPanel from "@/components/JourneyPanel";

const TYPE_META = {
  login: { icon: LogIn, color: "#22D3EE", tag: "Login" },
  signup: { icon: UserPlus, color: "#A3E635", tag: "New Member" },
  money_card: { icon: DollarSign, color: "#4ADE80", tag: "Card $" },
  money_coin: { icon: Coins, color: "#F59E0B", tag: "Coin BLC" },
  registration: { icon: ClipboardList, color: "#F472B6", tag: "Registration" },
  tournament: { icon: Trophy, color: "#FACC15", tag: "Tournament" },
  ai: { icon: Brain, color: "#A78BFA", tag: "AI Usage" },
  booking: { icon: CalendarCheck, color: "#A1A1AA", tag: "Booking" },
  application: { icon: GraduationCap, color: "#FB923C", tag: "Application" },
};
const FILTERS = [
  ["all", "All"], ["login", "Logins"], ["signup", "Signups"], ["money_card", "Card $"],
  ["money_coin", "Coins"], ["registration", "Registrations"], ["tournament", "Tournaments"],
  ["ai", "AI"], ["booking", "Bookings"], ["application", "Applications"],
];

function timeAgo(iso) {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

const Stat = ({ label, value, color, testid, prev }) => (
  <div className="border border-pb-border bg-pb-elevated px-3 py-2" data-testid={testid}>
    <div className="font-display font-black text-xl leading-none" style={{ color }}>{value}</div>
    <div className="text-[9px] uppercase tracking-[0.15em] text-zinc-500 mt-1">{label}</div>
    {prev != null && <div className="text-[9px] text-zinc-600 mt-0.5">yesterday: {prev}</div>}
  </div>
);

function DayClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const midnight = new Date(now);
  midnight.setHours(24, 0, 0, 0);
  const s = Math.max(0, Math.floor((midnight - now) / 1000));
  const pad = (n) => String(n).padStart(2, "0");
  const left = `${pad(Math.floor(s / 3600))}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}`;
  return (
    <span className="flex items-center gap-2 text-[10px] font-mono font-black uppercase tracking-widest text-zinc-300 border border-pb-border bg-pb-elevated px-2.5 py-1" data-testid="activity-day-clock">
      <Clock className="w-3.5 h-3.5 text-pb-cyan" />
      <span data-testid="activity-local-time">{now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}</span>
      <span className="text-zinc-600">·</span>
      <span className="text-amber-400" data-testid="activity-day-countdown" title="Time until the daily counters flip at your local midnight">flips in {left}</span>
    </span>
  );
}

export default function ActivityFeed() {
  const [events, setEvents] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cat, setCat] = useState("all");
  const [q, setQ] = useState("");
  const [openRow, setOpenRow] = useState(null);

  const load = useCallback(async (category = cat, query = q) => {
    setLoading(true);
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const { data } = await api.get("/admin/activity", { params: { category, q: query || undefined, tz } });
      setEvents(data.events);
      setSummary(data.summary);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [cat, q]);

  useEffect(() => { load(); }, []); // eslint-disable-line
  useEffect(() => {
    const t = setInterval(() => load(), 30000);
    return () => clearInterval(t);
  }, [load]);
  useEffect(() => {
    const t = setTimeout(() => load(cat, q), 350);
    return () => clearTimeout(t);
  }, [cat, q]); // eslint-disable-line

  return (
    <div className="bg-pb-surface border border-pb-border p-5 mb-8" data-testid="activity-feed">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Bell className="w-5 h-5 text-pb-red" />
          <span className="font-display font-bold text-lg uppercase">Live Activity — Operations Feed</span>
          <span className="text-[10px] uppercase tracking-widest text-pb-cyan bg-pb-cyan/10 border border-pb-cyan/30 px-2 py-0.5">Auto-refresh 30s</span>
        </div>
        <div className="flex items-center gap-2">
          <DayClock />
          {summary?.day && (
            <span className="text-[9px] uppercase tracking-widest text-zinc-500 hidden sm:inline" data-testid="activity-day-window">
              24h window · resets midnight {summary.day.tz}
            </span>
          )}
          {summary && (
            <span data-testid="online-now-pill" title={(summary.online_names || []).join(", ")}
              className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-lime-400 border border-lime-400/40 bg-lime-400/10 px-2.5 py-1">
              <Wifi className="w-3.5 h-3.5" /> {summary.online_now} online now
            </span>
          )}
          <button data-testid="activity-refresh-btn" onClick={() => load()} disabled={loading}
            className="w-8 h-8 flex items-center justify-center border border-pb-border hover:border-pb-red hover:text-pb-red transition-colors disabled:opacity-50">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2 mb-4" data-testid="activity-summary">
          <Stat label="Logins Today" value={summary.logins_today} color="#22D3EE" testid="stat-logins" prev={summary.yesterday?.logins} />
          <Stat label="New Members" value={summary.signups_today} color="#A3E635" testid="stat-signups" prev={summary.yesterday?.signups} />
          <Stat label="Card Revenue" value={`$${Number(summary.card_revenue_today).toFixed(2)}`} color="#4ADE80" testid="stat-card-rev" prev={summary.yesterday != null ? `$${Number(summary.yesterday.card_revenue).toFixed(2)}` : null} />
          <Stat label="Coin Volume" value={`${summary.coin_volume_today} BLC`} color="#F59E0B" testid="stat-coin-vol" />
          <Stat label="Registrations" value={summary.registrations_today} color="#F472B6" testid="stat-regs" prev={summary.yesterday?.registrations} />
          <div className="border border-pb-border bg-pb-elevated px-3 py-2" data-testid="stat-ai">
            <div className="font-display font-black text-xl leading-none text-[#A78BFA]">
              ${Number(summary.ai_cost_today || 0).toFixed(3)}
            </div>
            <div className="text-[9px] uppercase tracking-[0.15em] text-zinc-500 mt-1">AI Spend Est · {summary.ai_calls_today} calls</div>
            {(summary.ai_spend || []).slice(0, 3).map((m, i) => (
              <div key={i} className="flex justify-between text-[9px] text-zinc-400 mt-0.5" data-testid={`ai-spend-${m.model}`}>
                <span className="truncate mr-1 font-mono">{m.model}</span>
                <span className="text-[#A78BFA] font-bold shrink-0">{m.calls}× · ${Number(m.cost).toFixed(3)}</span>
              </div>
            ))}
          </div>
          <Stat label="Site Minutes" value={summary.site_minutes_today} color="#FACC15" testid="stat-minutes" />
          <div className="border border-pb-border bg-pb-elevated px-3 py-2" data-testid="stat-top-time">
            <div className="text-[9px] uppercase tracking-[0.15em] text-zinc-500 mb-1 flex items-center gap-1"><Clock className="w-3 h-3" /> Top Time Today</div>
            {(summary.top_time_today || []).slice(0, 3).map((t, i) => (
              <div key={i} className="flex justify-between text-[10px] text-zinc-300"><span className="truncate mr-1">{t.name}</span><span className="text-amber-400 font-bold shrink-0">{t.minutes}m</span></div>
            ))}
            {!(summary.top_time_today || []).length && <div className="text-[10px] text-zinc-600">No sessions yet</div>}
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-1.5 mb-3">
        {FILTERS.map(([k, label]) => (
          <button key={k} data-testid={`activity-filter-${k}`} onClick={() => setCat(k)}
            className={`px-2.5 py-1 text-[9px] font-black uppercase tracking-widest border transition-colors ${cat === k ? "border-pb-red text-pb-red bg-pb-red/10" : "border-pb-border text-zinc-500 hover:text-zinc-300"}`}>
            {label}
          </button>
        ))}
        <div className="relative ml-auto">
          <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-zinc-600" />
          <input data-testid="activity-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or action…"
            className="bg-pb-elevated border border-pb-border focus:border-pb-cyan outline-none pl-7 pr-2 py-1.5 text-xs w-48" />
        </div>
      </div>

      {events.length === 0 ? (
        <div className="text-sm text-zinc-600 py-4" data-testid="activity-empty">{loading ? "Loading…" : "No matching activity — logins, purchases, registrations and AI usage will appear here live."}</div>
      ) : (
        <div className="max-h-96 overflow-y-auto divide-y divide-pb-border/40" data-testid="activity-list">
          {events.map((e, i) => {
            const meta = TYPE_META[e.type] || TYPE_META.booking;
            const Icon = meta.icon;
            const isOpen = openRow === i;
            return (
              <div key={i} data-testid={`activity-row-${i}`}>
              <div className={`flex items-center gap-3 py-2.5 cursor-pointer transition-colors hover:bg-pb-elevated/40 ${isOpen ? "bg-pb-elevated/40" : ""}`}
                data-testid={`activity-row-toggle-${i}`} onClick={() => setOpenRow(isOpen ? null : i)} title="Click to see this member's exact path through the site">
                <Avatar src={e.avatar} name={e.user} size={30} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">
                    <span className="font-semibold text-white">{e.user}</span>{" "}
                    <span className="text-zinc-400">{e.label}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 border"
                      style={{ color: meta.color, borderColor: `${meta.color}55`, background: `${meta.color}11` }}>
                      <Icon className="w-3 h-3" /> {meta.tag}
                    </span>
                    <span className="text-[10px] text-zinc-600 font-mono">{timeAgo(e.at)}</span>
                  </div>
                  {e.detail && (
                    <div className="text-[10px] text-zinc-500 italic truncate mt-0.5" data-testid={`activity-detail-${i}`} title={e.detail}>{e.detail}</div>
                  )}
                  {e.contact && (
                    <div className="flex items-center gap-1 text-[11px] text-pb-cyan mt-1 font-mono" data-testid={`activity-contact-${i}`}>
                      <Phone className="w-3 h-3 shrink-0" /> {e.contact}
                    </div>
                  )}
                </div>
                {e.amount != null && (
                  <span className={`font-display font-black shrink-0 ${e.currency === "BLC" ? "text-amber-400" : "text-pb-red"}`}>
                    {e.currency === "BLC" ? `${Number(e.amount).toFixed(2)} BLC` : `$${Number(e.amount).toFixed(2)}`}
                  </span>
                )}
                <ChevronDown className={`w-4 h-4 text-zinc-600 shrink-0 transition-transform ${isOpen ? "rotate-180 text-pb-cyan" : ""}`} />
              </div>
              {isOpen && <JourneyPanel userId={e.user_id} name={e.user} testid={`journey-${i}`} />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
